import { useEffect } from 'react';
import { useThemeCustom } from '../hooks/useThemeCustom';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { muiThemeConfig } from '../theme/muiThemeConfig'; // Fase A: ambiente MUI PINADO (mesmo do design-package)
import { shape } from '../theme/designTokens';
import { applyColorModeAttribute, applyThemeCssVars } from '../theme/resolveThemeTokens';
import { ensureHeadingFont } from '../theme/headingFont';

const TRANSITION = 'all 0.2s ease-in-out';

/**
 * Motor único de estilo do painel. Junta as três camadas (docs/ANALISE-TEMAS-MARCA-VS-OPERACAO.md):
 *   - ESCALA OPERACIONAL do modo → vem do tokens.css pelo atributo `data-color-mode` no <html>;
 *   - ACENTO do nicho no modo + fonte de título → CSS vars inline no :root (resolveThemeTokens);
 *   - tema MUI com `palette.mode` real, para hover/selected/disabled/skeleton do MUI nascerem
 *     calculados para o fundo certo.
 */
export default function ThemeApplier({ children, baseTheme }) {
  const { themeTokens: t } = useThemeCustom();
  const c = t.colors;

  const muiTheme = createTheme({
    ...baseTheme,
    palette: {
      ...baseTheme.palette,
      mode: t.mode,
      primary: { main: c.primary, dark: c.primaryDark, light: c.primaryLight, contrastText: c.onPrimary },
      secondary: { main: c.secondary },
      background: { default: c.background, paper: c.backgroundPaper },
      text: { primary: c.text, secondary: c.textSecondary, disabled: c.textDisabled },
      success: { main: c.success },
      error: { main: c.error },
      warning: { main: c.warning },
      info: { main: c.info },
      divider: c.divider,
    },
    typography: {
      ...baseTheme.typography,
      fontFamily: 'var(--font-family)',
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusCard,
            boxShadow: t.scale['--shadow-card'],
            transition: TRANSITION,
            '&:hover': {
              boxShadow: t.scale['--shadow-hover'],
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusCard,
            // MUI escurece o Paper no modo escuro com um gradiente de "elevação"; a superfície
            // do painel é a da escala, sem véu por cima.
            backgroundImage: 'none',
          },
          elevation1: { boxShadow: t.scale['--shadow-card'] },
          elevation2: { boxShadow: t.scale['--shadow-card'] },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: shape.radiusSm,
            textTransform: 'none',
            fontWeight: 500,
            transition: TRANSITION,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: t.scale['--shadow-card'],
              transform: 'translateY(-1px)',
            },
          },
          contained: {
            boxShadow: t.scale['--shadow-card'],
            '&:hover': { boxShadow: t.scale['--shadow-hover'] },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: shape.radiusSm },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: shape.radiusLg,
            boxShadow: t.scale['--shadow-hover'],
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: shape.radiusSm,
              transition: TRANSITION,
              '&:hover': { boxShadow: `0 0 0 2px ${c.primarySoft}` },
              '&.Mui-focused': { boxShadow: `0 0 0 2px ${c.primarySoft}` },
            },
          },
        },
      },
    },
  }, muiThemeConfig); // Fase A: deep-merge do ambiente pinado (typography line-heights, MuiSwitch chunky, densidade)

  // Modo no <html> (troca a escala do tokens.css) + acento/título inline no :root.
  useEffect(() => {
    applyColorModeAttribute(t.mode);
    applyThemeCssVars(t.cssVars);
    ensureHeadingFont(t.fontHeading);
  }, [t]);

  return <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>;
}

ThemeApplier.propTypes = {
  children: PropTypes.node.isRequired,
  baseTheme: PropTypes.object.isRequired,
};
